/**
 * Welcome Popup — Layer A
 * File: assets/welcome-popup.js
 *
 * Depends on: lead-capture-utils.js (LiftSetsCapture global)
 * 
 * Two-step flow:
 * Step 1: Email capture → validates, then lets Shopify form submit naturally
 *         (Shopify handles CAPTCHA + customer creation + page redirect)
 * Step 2: On page reload, detects pending step 2 → shows buyer type selection
 *
 * Trigger: 6-second delay on page load (Step 1) / immediate on reload (Step 2)
 */

(function () {
  'use strict';

  var LC = window.LiftSetsCapture;
  if (!LC) return;

  /* ============================================================
     DOM REFERENCES
     ============================================================ */

  var overlay      = document.getElementById('ls-welcome-popup');
  if (!overlay) return;

  var step1        = document.getElementById('ls-welcome-step1');
  var step2        = document.getElementById('ls-welcome-step2');
  var form         = document.getElementById('ls-welcome-form');
  var emailInput   = document.getElementById('ls-welcome-email');
  var tagsInput    = document.getElementById('ls-welcome-tags');
  var submitBtn    = document.getElementById('ls-welcome-submit');
  var errorEl      = document.getElementById('ls-welcome-error');
  var closeBtn     = overlay.querySelector('.ls-popup-close');
  var skipBtn      = document.getElementById('ls-welcome-skip');
  var buyerBtns    = overlay.querySelectorAll('.ls-buyer-btn');

  var isOpen       = false;

  /* ============================================================
     SHOULD SHOW (Step 1 — first visit)
     ============================================================ */

  function shouldShow() {
    if (LC.hasSubmitted()) return false;
    if (LC.isLoggedIn()) return false;
    if (LC.isExcludedPage()) return false;
    if (LC.withinDismissalCooldown('liftsets_popup_dismissed', 14 * 24)) return false;
    if (LC.getSession('liftsets_popup_dismissed_session') && LC.getPageViews() < 3) return false;
    return true;
  }

  /* ============================================================
     OPEN / CLOSE POPUP
     ============================================================ */

  function openPopup() {
    overlay.style.display = 'flex';
    LC.lockScroll();
    void overlay.offsetHeight;
    overlay.classList.add('ls-popup-visible');
    isOpen = true;
    LC.fireEvent('popup_shown', { popup_type: 'welcome' });
  }

  function closePopup(method) {
    if (!isOpen) return;

    overlay.classList.remove('ls-popup-visible');
    isOpen = false;

    setTimeout(function () {
      overlay.style.display = 'none';
      LC.unlockScroll();
    }, 300);

    // Track dismissal if they haven't submitted yet
    if (!LC.hasSubmitted()) {
      LC.setLocal('liftsets_popup_dismissed', String(Date.now()));
      LC.setSession('liftsets_popup_dismissed_session', '1');
      LC.fireEvent('popup_closed', { popup_type: 'welcome', method: method || 'unknown' });
    }
  }

  /* ============================================================
     ERROR HANDLING
     ============================================================ */

  function showError(msg) {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
    emailInput.classList.add('ls-input-error');
  }

  function clearError() {
    errorEl.style.display = 'none';
    errorEl.textContent = '';
    emailInput.classList.remove('ls-input-error');
  }

  /* ============================================================
     STEP 1 — FORM SUBMIT HANDLER
     
     Validates email client-side. If valid:
     1. Saves email to localStorage for Step 2
     2. Marks as submitted (suppresses future popups)
     3. Fires GA4 events
     4. Lets the form submit naturally (Shopify handles CAPTCHA)
     5. Page refreshes → Step 2 shows on reload
     ============================================================ */

  async function handleSubmit(e) {
    clearError();

    var email = emailInput.value.trim().toLowerCase();

    // Validate email format
    if (!LC.isValidEmail(email)) {
      e.preventDefault();
      showError('Please enter a valid email address.');
      LC.fireEvent('popup_email_error', { popup_type: 'welcome', error_type: 'invalid_email' });
      return;
    }

    // Check disposable email
    await LC.loadDisposableDomains();
    if (LC.isDisposableEmail(email)) {
      e.preventDefault();
      showError('Please use a non-temporary email address.');
      LC.fireEvent('popup_email_error', { popup_type: 'welcome', error_type: 'disposable_email' });
      return;
    }

    // ✅ Validation passed — save state BEFORE the form submits & page refreshes
    LC.markSubmitted();
    LC.setLocal('liftsets_pending_step2', email);
    LC.fireEvent('popup_email_submitted', { popup_type: 'welcome', buyer_type: '' });
    LC.fireEnhancedConversion(email);

    // Let the form submit naturally — DO NOT preventDefault here
    // Shopify handles CAPTCHA, creates the customer, and redirects back
  }

  /* ============================================================
     STEP 2 — BUYER TYPE SELECTION (on page reload)
     ============================================================ */

  function handleBuyerType(type) {
    var pendingEmail = LC.getLocal('liftsets_pending_step2');

    LC.fireEvent('popup_step2_completed', { popup_type: 'welcome', buyer_type: type });

    // Clean up — Step 2 is done
    LC.removeLocal('liftsets_pending_step2');

    // Commercial buyers go to trade quote page
    if (type === 'commercial') {
      window.location.href = '/pages/trade-quote';
      return;
    }

    // Home / PT — close the popup
    closePopup();
  }

  /* ============================================================
     EVENT LISTENERS
     ============================================================ */

  form.addEventListener('submit', handleSubmit);

  emailInput.addEventListener('input', function () {
    if (errorEl.style.display !== 'none') clearError();
  });

  closeBtn.addEventListener('click', function () { closePopup('x_button'); });

  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closePopup('overlay');
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && isOpen) closePopup('escape');
  });

  for (var i = 0; i < buyerBtns.length; i++) {
    (function (btn) {
      btn.addEventListener('click', function () {
        handleBuyerType(btn.getAttribute('data-buyer-type'));
      });
    })(buyerBtns[i]);
  }

  skipBtn.addEventListener('click', function () {
    LC.removeLocal('liftsets_pending_step2');
    closePopup('skip');
  });

  /* ============================================================
     INIT — Check if we need to show Step 1 or Step 2
     ============================================================ */

  var pendingStep2 = LC.getLocal('liftsets_pending_step2');

 if (pendingStep2) {
    // Page just reloaded after Shopify form submit
    // Remove #contact_form hash that Shopify adds (causes scroll to footer)
    if (window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    // Scroll to top
    window.scrollTo(0, 0);

    // Hide any Shopify form errors on the footer newsletter
    var footerErrors = document.querySelectorAll('.form__message--error');
    footerErrors.forEach(function(el) { el.style.display = 'none'; });

    step1.style.display = 'none';
    step2.style.display = 'block';
    openPopup();

  } else if (shouldShow()) {
    // Delay popup: 15s for search traffic, 6s for direct/social
    var fromSearch = document.referrer && (
      document.referrer.indexOf('google.') !== -1 ||
      document.referrer.indexOf('bing.') !== -1 ||
      document.referrer.indexOf('yahoo.') !== -1 ||
      document.referrer.indexOf('duckduckgo.') !== -1 ||
      document.referrer.indexOf('ecosia.') !== -1
    );
    var delay = fromSearch ? 15000 : 6000;
    setTimeout(function () {
      openPopup();
      setTimeout(function () { emailInput.focus(); }, 350);
    }, delay);
  }

})();